<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  name: 'DoctorConsultModal',
  components: { Multiselect },
  props: {
    value: { type: Boolean, default: false },
    appointment: { type: Object, default: null },
    existing: { type: Object, default: null }
  },
  data() {
    return {
      activeTab: 'medication',
      // Medication state
      medicineOptions: [
        'Paracetamol 500mg',
        'Ibuprofen 200mg',
        'Amoxicillin 500mg',
        'Atorvastatin 10mg',
        'Metformin 500mg',
        'Omeprazole 20mg',
        'Azithromycin 250mg'
      ],
      selectedMedicines: [], // array of names
      medicineRows: [], // [{name, frequency, timesPerDay}]

      // Diagnosis state
      diagnosisOptions: [
        'Hypertension',
        'Diabetes Mellitus Type 2',
        'Hyperlipidemia',
        'Upper Respiratory Infection',
        'Gastritis',
        'Migraine',
        'Back Pain'
      ],
      selectedDiagnoses: [],

      // Result/prescription
      prescriptionCreated: null, // object when created
      previewOpen: false
    };
  },
  computed: {
    showModal: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); }
    },
    hasExisting() {
      return !!this.existing;
    },
    currentPrescription() {
      return this.prescriptionCreated || this.buildPrescription();
    }
  },
  watch: {
    // When opening for a different appointment, reset unless existing provided
    appointment: {
      immediate: true,
      handler() {
        this.resetIfNeeded();
      }
    },
    existing: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // Load existing into view mode
          this.prescriptionCreated = newVal;
          this.selectedMedicines = (newVal.medications || []).map(m => m.name);
          this.medicineRows = (newVal.medications || []).map(m => ({ name: m.name, frequency: m.frequency, timesPerDay: m.timesPerDay }));
          this.selectedDiagnoses = newVal.diagnoses || [];
          this.previewOpen = true;
          this.activeTab = 'medication';
        }
      }
    },
    selectedMedicines() {
      // Ensure medicineRows contain each selected medicine
      const set = new Set(this.selectedMedicines);
      // Add new selections
      this.selectedMedicines.forEach(name => {
        if (!this.medicineRows.find(r => r.name === name)) {
          this.medicineRows.push({ name, frequency: 'Once daily', timesPerDay: 1 });
        }
      });
      // Remove deselected
      this.medicineRows = this.medicineRows.filter(r => set.has(r.name));
    }
  },
  methods: {
    resetIfNeeded() {
      if (!this.existing) {
        this.activeTab = 'medication';
        this.selectedMedicines = [];
        this.medicineRows = [];
        this.selectedDiagnoses = [];
        this.prescriptionCreated = null;
        this.previewOpen = false;
      }
    },
    close() {
      this.$emit('closed');
      this.showModal = false;
    },
    saveDiagnosis() {
      // Nothing special beyond keeping selection; provide quick feedback
      this.$bvToast && this.$bvToast.toast('Diagnoses saved in session', { title: 'Saved', variant: 'success', solid: true });
    },
    saveMedicines() {
      this.$bvToast && this.$bvToast.toast('Medicines saved in session', { title: 'Saved', variant: 'success', solid: true });
    },
    buildPrescription() {
      const medications = this.medicineRows.map(r => ({ name: r.name, frequency: r.frequency, timesPerDay: Number(r.timesPerDay) || 0 }));
      return {
        appointmentId: this.appointment ? this.appointment.id : null,
        patientName: this.appointment ? this.appointment.patientName : '',
        createdAt: new Date().toISOString(),
        medications,
        diagnoses: [...this.selectedDiagnoses]
      };
    },
    addPrescription() {
      const presc = this.buildPrescription();
      this.prescriptionCreated = presc;
      this.$emit('saved', presc);
      this.$bvToast && this.$bvToast.toast('Prescription created', { title: 'Success', variant: 'success', solid: true });
    },
    getPrescriptionJSON() {
      return JSON.stringify(this.prescriptionCreated || this.buildPrescription(), null, 2);
    },
    downloadPrescription() {
      const blob = new Blob([this.getPrescriptionJSON()], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const name = (this.appointment ? this.appointment.patientName.replace(/\s+/g, '_') : 'prescription') + '_prescription.json';
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    async sharePrescription() {
      const text = this.getPrescriptionJSON();
      if (navigator.share) {
        try {
          await navigator.share({ title: 'Prescription', text });
        } catch (e) {
          // ignore cancel
        }
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        this.$bvToast && this.$bvToast.toast('Prescription copied to clipboard', { title: 'Copied', variant: 'info', solid: true });
      }
    },
    formatDate(iso) {
      if (!iso) return '-';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleString();
    }
  }
};
</script>

<template>
  <b-modal v-model="showModal" size="lg" centered no-close-on-backdrop hide-footer title="Consultation">
    <div v-if="appointment" class="mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">{{ appointment.patientName }}</h5>
          <small class="text-muted">Time: {{ appointment.time }} | Reason: {{ appointment.reason }}</small>
        </div>
        <div>
          <b-button variant="outline-secondary" size="sm" @click="close">Close</b-button>
        </div>
      </div>
    </div>

    <b-tabs content-class="mt-3">
      <b-tab title="Medication" :active="activeTab==='medication'" key="medication">
        <div class="mb-3">
          <label class="form-label">Select Medicines</label>
          <Multiselect v-model="selectedMedicines" :options="medicineOptions" :multiple="true" placeholder="Select medicines" label="" track-by="" />
        </div>

        <div class="mb-2">
          <b-button size="sm" variant="primary" @click="saveMedicines">Save Medicines</b-button>
        </div>

        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead>
              <tr>
                <th>Medicine</th>
                <th>Frequency</th>
                <th>No. of times/day</th>
                <th>Remove</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in medicineRows" :key="row.name">
                <td class="align-middle">{{ row.name }}</td>
                <td>
                  <input class="form-control form-control-sm" v-model="row.frequency" placeholder="e.g. Once daily, BID, TID" />
                </td>
                <td style="width: 140px;">
                  <input type="number" min="0" class="form-control form-control-sm" v-model.number="row.timesPerDay" />
                </td>
                <td class="text-center">
                  <b-button size="sm" variant="outline-danger" @click="selectedMedicines = selectedMedicines.filter(n => n !== row.name)">Remove</b-button>
                </td>
              </tr>
              <tr v-if="!medicineRows.length">
                <td colspan="4" class="text-center text-muted">No medicines selected</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex flex-wrap gap-2">
          <b-button variant="success" class="me-2" @click="addPrescription">Add Prescription</b-button>
          <b-button variant="secondary" class="me-2" :disabled="!prescriptionCreated" @click="previewOpen = !previewOpen">{{ previewOpen ? 'Hide' : 'View' }} Prescription</b-button>
          <b-button variant="info" class="me-2" :disabled="!prescriptionCreated" @click="downloadPrescription">Download</b-button>
          <b-button variant="primary" :disabled="!prescriptionCreated" @click="sharePrescription">Share</b-button>
        </div>

        <div v-if="previewOpen && prescriptionCreated" class="mt-3">
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h5 class="mb-1">Prescription</h5>
                  <div class="text-muted small">Patient: {{ currentPrescription.patientName }} | Date: {{ formatDate(currentPrescription.createdAt) }}</div>
                </div>
                <div v-if="currentPrescription.appointmentId" class="text-muted small">Appointment #{{ currentPrescription.appointmentId }}</div>
              </div>
              <hr>
              <div class="mb-2">
                <h6>Diagnoses</h6>
                <ul class="mb-0" v-if="currentPrescription.diagnoses && currentPrescription.diagnoses.length">
                  <li v-for="(d, i) in currentPrescription.diagnoses" :key="i">{{ d }}</li>
                </ul>
                <p v-else class="text-muted mb-0">No diagnoses specified</p>
              </div>
              <div class="mt-3">
                <h6>Medications</h6>
                <div class="table-responsive">
                  <table class="table table-bordered table-sm mb-0">
                    <thead class="thead-light">
                      <tr>
                        <th>Medicine</th>
                        <th>Frequency</th>
                        <th>Times/day</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(m, idx) in currentPrescription.medications" :key="m.name + idx">
                        <td>{{ m.name }}</td>
                        <td>{{ m.frequency || '-' }}</td>
                        <td>{{ m.timesPerDay || 0 }}</td>
                      </tr>
                      <tr v-if="!currentPrescription.medications || !currentPrescription.medications.length">
                        <td colspan="3" class="text-center text-muted">No medications added</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <b-tab title="Diagnosis" key="diagnosis">
        <div class="mb-3">
          <label class="form-label">Select Diagnoses</label>
          <Multiselect v-model="selectedDiagnoses" :options="diagnosisOptions" :multiple="true" placeholder="Select diagnoses" />
        </div>

        <div class="mb-2">
          <b-button size="sm" variant="primary" @click="saveDiagnosis">Save Diagnoses</b-button>
        </div>

        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead>
              <tr>
                <th>#</th>
                <th>Diagnosis</th>
                <th>Remove</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(d, i) in selectedDiagnoses" :key="d">
                <td class="align-middle" style="width: 60px;">{{ i + 1 }}</td>
                <td class="align-middle">{{ d }}</td>
                <td class="text-center" style="width: 120px;">
                  <b-button size="sm" variant="outline-danger" @click="selectedDiagnoses = selectedDiagnoses.filter(x => x !== d)">Remove</b-button>
                </td>
              </tr>
              <tr v-if="!selectedDiagnoses.length">
                <td colspan="3" class="text-center text-muted">No diagnoses selected</td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-tab>
    </b-tabs>
  </b-modal>
</template>

<style scoped>
/* minimal styling tweaks if needed */
</style>
